<template>
  <BaseLocationSection
    :id="id"
    ref="baseLocationSection"
    v-model="internalValue"
    :parent-entity="parentEntity"
    :mileages="mileages"
    :line-items="lineItems"
    :errors="errors"
    :accessorial-codes="accessorialCodes"
    :editable="editable"
    :hide-notes="hideNotes"
    :can-add-stops="canAddStops"
    @change="emit('change', $event)"
    @update:line-items="emit('update:line-items', $event)"
  />
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import { useUid, useValidations } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  LineItem,
  Order,
  Shipment,
  ShipmentLineItem,
  ShipmentStatus,
  ShippingPoint,
  StopMileageResponse,
} from '@/model';
import { useAccessorialCodeStore } from '@/stores/AccessorialCode';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { isStatusEqualOrBefore } from '@/util';

import BaseLocationSection from '@/components/location/BaseLocationSection.vue';

const internalValue = defineModel({
  type: Array as PropType<ShippingPoint[]>,
  default: () => [],
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  /** An array of vuelidate errors for the stops array as a whole. */
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  hideNotes: {
    type: Boolean,
    default: () => false,
  },
});
const { id, lineItems, errors, editable, hideNotes } = toRefs(props);
const emit = defineEmits<{
  (event: 'change', payload: { isDirty: boolean; numDirty: number }): void;
  (event: 'update:line-items', lineItems: ShipmentLineItem[]): void;
}>();

defineExpose({
  reset: () => baseLocationSection.value?.reset(),
});

const baseLocationSection = useTemplateRef('baseLocationSection');

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const { parentEntity } = useParentEntityStore();
const { uid } = useUid('location-section');
useValidations(internalValue, {}, uid);

const mileages: Ref<Loadable<StopMileageResponse[]>> = ref({ loading: false });
const mileageCancelToken: Ref<CancelTokenSource | undefined> = ref();

const canAddStops = computed(() => {
  const shipment = parentEntity.value;
  if (!(shipment instanceof Shipment)) {
    return false;
  }

  if (
    editable.value &&
    isStatusEqualOrBefore(shipment.status, ShipmentStatus.CARRIER_ASSIGNED)
  ) {
    return true;
  }

  // Users with these special permission can edit even when `editable` is false
  if (isStatusEqualOrBefore(shipment.status, ShipmentStatus.BOOKED)) {
    return (
      authorization.hasAuthority('shipment', 'updateLockedFields') ||
      authorization.hasAuthority('shipment', 'updateBookedShipmentLocations')
    );
  }

  return false;
});

const findMileages = async () => {
  if (mileageCancelToken.value) {
    findMileageDebounced.cancel();
    mileageCancelToken.value.cancel();
  }
  mileageCancelToken.value = axios.CancelToken.source();

  if (
    parentEntity.value instanceof Order &&
    parentEntity.value.totalMileage !== undefined
  ) {
    const totalMileage = parentEntity.value.totalMileage;
    mileages.value = {
      loading: false,
      data: [
        new StopMileageResponse({
          originSequenceNumber: 1,
          destinationSequenceNumber: 2,
          mileage: totalMileage,
        }),
      ],
    };
    return;
  }

  try {
    mileages.value.error = undefined;
    mileages.value.loading = true;
    mileages.value.data = await api.route.findMileage(
      internalValue.value,
      mileageCancelToken.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching mileage', e);
    mileages.value.error = e;
  } finally {
    mileages.value.loading = false;
  }
};
const findMileageDebounced = debounce(findMileages, 1000);

watch(
  internalValue,
  (newVal: ShippingPoint[]) => {
    if (isEqual(newVal, internalValue)) {
      return;
    }

    findMileageDebounced();
  },
  { immediate: true },
);

watch(
  editable,
  (newVal: boolean) => {
    if (newVal) {
      loadRequestableAccessorialCodes();
    }
  },
  { immediate: true },
);
</script>
